import { ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo, useRef, use } from "react";
import { Document, Page } from "react-pdf";
import { Button } from "./ui/button";

export default function PDFViewer({ pdfFile, onRefreshRequest }) {
  console.log('PDFViewer received pdfFile:', pdfFile);

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [pageWidth, setPageWidth] = useState(null);

  const pageRef = useRef(null);
  const containerRef = useRef(null);

  const isSharePointUrl = useMemo(() => {
    return typeof pdfFile === 'string' && pdfFile.includes('sharepoint.com');
  }, [pdfFile]);
  
   // ✅ Memoize the file prop to prevent unnecessary re-renders
  const memoizedFile = useMemo(() => {
    if (!pdfFile) return null;
    
    console.log('PDFViewer memoizing pdfFile:', pdfFile);
    
    // If it's a SharePoint URL, add credentials
    if (isSharePointUrl) {
      return pdfFile;
    }
    
    return pdfFile;
  }, [pdfFile, isSharePointUrl]);

  // Normalize file for react-pdf: supports string URL, File, or { data: ArrayBuffer }
  const fileForReactPdf = useMemo(() => {
    if (!memoizedFile) return null;

    if (typeof memoizedFile === "string") return memoizedFile;
    if (memoizedFile instanceof File) return memoizedFile;
    if (memoizedFile && memoizedFile.data instanceof ArrayBuffer) {
      return { data: memoizedFile.data };
    }
    if (memoizedFile instanceof Blob) return URL.createObjectURL(memoizedFile);

    return null;
  }, [memoizedFile]);

  useEffect(() => {
    setPageNumber(1);
    setError(null);
    setLoading(true);
  }, [memoizedFile]);
  
  // when file changes, reset state
  useEffect(() => {
    if (memoizedFile) {
      console.log('File changed, resetting PDF viewer state');
      setLoading(true);
      setError(null);
      setNumPages(null);
      setPageNumber(1);
      pageRef.current = null;
    }
  }, [memoizedFile]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPageWidth(containerWidth - 40);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (memoizedFile instanceof Blob && !(memoizedFile instanceof File)) {
      const url = URL.createObjectURL(memoizedFile);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setObjectUrl(null);
  }, [memoizedFile]);

  const docKey = useMemo(() => {
    return typeof fileForReactPdf === "string"
      ? fileForReactPdf
      : memoizedFile?.name || "local-pdf";
  }, [fileForReactPdf, memoizedFile]);

  function onDocumentLoadSuccess({ numPages: n }) {
    setNumPages(n);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(err) {
    setLoading(false);
    const msg = (err && err.message) || String(err) || "Failed to load PDF";

        console.error('PDF Load Error:', err);
    
    // ✅ Better error detection for SharePoint token expiration
    if (msg.includes("sendWithPromise")) {
      setError("PDF worker failed to initialize. Please refresh.");
    } else if (msg.includes("401") || msg.includes("403") || msg.includes("expired")) {
      setError("SharePoint access token expired. Click refresh to get a new link.");
    } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError") || msg.includes("ERR_CONNECTION_RESET")) {
      setError("Network error loading PDF. SharePoint link may have expired.");
    } else {
      setError(msg);
    }
  }

  useEffect(() => {
    if (memoizedFile) {
      setLoading(true);
      setError(null);
      setNumPages(null);
      setPageNumber(1);
    }
  }, [memoizedFile]);

    const handleRefresh = () => {
    if (onRefreshRequest) {
      console.log('Requesting file list refresh to get new SharePoint token...');
      onRefreshRequest();
    } else {
      window.location.reload();
    }
  };

  if (!memoizedFile) {
    console.log('PDFViewer: No PDF file provided');
    return (
      <div className="flex h-96 items-center justify-center rounded-lg bg-gray-100">
        <p className="text-gray-500">No PDF file selected</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center rounded-lg bg-red-50">
        <div className="text-center">
          <p className="font-medium text-red-600">Error loading PDF</p>
          <p className="mt-1 text-sm text-red-500">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleRefresh}
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  const canPrev = pageNumber > 1;
  const canNext = numPages ? pageNumber < numPages : false;

  return (
    <div className="w-full rounded-lg bg-black shadow-lg">
      <div className="flex items-center justify-between border-b bg-gray-50 p-4">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => setPageNumber(p => Math.max(p - 1, 1))} disabled={!canPrev || loading}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-2 text-sm font-medium">
            {loading ? "Loading..." : `Page ${pageNumber} of ${numPages || "?"}`}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPageNumber(p => Math.min(p + 1, numPages || p))} disabled={!canNext || loading}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => setScale(s => Math.max(s - 0.2, 0.5))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="min-w-12 text-sm text-gray-600">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="sm" onClick={() => setScale(s => Math.min(s + 0.2, 3.0))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setRotation(r => (r + 90) % 360)}>
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* CONTAINER NEEDS TO SHOW FULL DOCUMENT HEIGHT */}

      <div ref={containerRef} className="bg-gray-800 p-4" style={{ overflow: 'auto', maxHeight: '80vh' }}>
        <div className="flex justify-center items-start min-h-full">
          <Document
            key={docKey}                             
            file={objectUrl || fileForReactPdf}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="flex h-96 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500" />
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              width={pageWidth}
              rotate={rotation}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={
                <div className="flex h-96 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-gray-400" />
                </div>
              }
            />
          </Document>
        </div>
      </div>
    </div>
  );
}


